import { useEffect, useRef, useState, useCallback } from 'react';
import { useGetProductsWithTariffsQuery, productCatalogueApi } from '@/services/product-catalogue/pc-api';
import { ProductTariffQueryInput } from '@/services/product-catalogue/pc-api';
import { useAppDispatch } from '@/store';

interface ProductPollingState {
  productId: string;
  triggerReason: 'description_edit' | 'hts_edit' | 'creation' | 'bulk_upload';
  pollCount: number;
  startTime: number;
  isPolling: boolean;
}

interface BulkPollingState {
  isPolling: boolean;
  startTime: number;
  pollCount: number;
  pendingProductIds: Set<string>;
}

interface UseProductAnalysisPollingOptions {
  queryParams: ProductTariffQueryInput;
  enabled: boolean;
}

export const useProductAnalysisPolling = ({ queryParams, enabled }: UseProductAnalysisPollingOptions) => {
  const dispatch = useAppDispatch();
  const [productPollingStates, setProductPollingStates] = useState<Map<string, ProductPollingState>>(new Map());
  const [bulkPollingState, setBulkPollingState] = useState<BulkPollingState>({
    isPolling: false,
    startTime: 0,
    pollCount: 0,
    pendingProductIds: new Set()
  });
  
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bulkPollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get the main query for refetching
  const { refetch } = useGetProductsWithTariffsQuery(queryParams, {
    skip: !enabled,
    refetchOnMountOrArgChange: true,
  });

  // Check if a product needs a pending status
  const isProductPending = useCallback((productId: string): boolean => {
    return productPollingStates.has(productId) || bulkPollingState.pendingProductIds.has(productId);
  }, [productPollingStates, bulkPollingState]);

  // Add individual product to polling
  const addProductPolling = useCallback((productId: string, triggerReason: 'description_edit' | 'hts_edit' | 'creation') => {
    if (!productId) {
      console.warn('Attempted to add polling for undefined productId');
      return;
    }
    
    setProductPollingStates(prev => {
      // Check if product is already being polled using the current state
      if (prev.has(productId)) {
        return prev;
      }
      
      const newMap = new Map(prev);
      newMap.set(productId, {
        productId,
        triggerReason,
        pollCount: 0,
        startTime: Date.now(),
        isPolling: true
      });
      return newMap;
    });
  }, []);

  // Add bulk upload products to polling
  const addBulkPolling = useCallback((productIds: string[]) => {
    setBulkPollingState({
      isPolling: true,
      startTime: Date.now(),
      pollCount: 0,
      pendingProductIds: new Set(productIds)
    });
  }, []);

  // Clear bulk polling
  const clearBulkPolling = useCallback(() => {
    setBulkPollingState({
      isPolling: false,
      startTime: 0,
      pollCount: 0,
      pendingProductIds: new Set()
    });
  }, []);

  // Individual product polling logic
  const pollIndividualProducts = useCallback(async () => {
    if (productPollingStates.size === 0) return;

    const currentTime = Date.now();
    const productsToRemove: string[] = [];

    try {
      const result = await refetch();
      
      if (result.data?.data) {
        const products = result.data.data;
        const productMap = new Map(products.map(p => [p._id, p]));

        productPollingStates.forEach((pollingState, productId) => {
          const product = productMap.get(productId);
          
          if (!product) {
            return;
          }
          
          // Check if analysis is complete - more robust completion check
          const hasRiskAnalysis = product?.riskAnalysis;
          const isAnalysisComplete = hasRiskAnalysis && 
              hasRiskAnalysis.riskLevel !== 'RUNNING' && 
              hasRiskAnalysis.riskLevel !== 'PENDING';
          
          
          if (isAnalysisComplete) {
            // Analysis complete - remove from polling
            productsToRemove.push(productId);
            
            // Invalidate cache for this product
            dispatch(productCatalogueApi.util.invalidateTags([
              { type: 'Product', id: productId },
              { type: 'Product', id: 'LIST' }
            ]));
          } else {
            // Check if we should stop polling (max polls or timeout)
            const timeElapsed = currentTime - pollingState.startTime;
            const maxPolls = pollingState.triggerReason === 'creation' ? 5 : 3; // More polls for creation
            const maxTime = pollingState.triggerReason === 'creation' ? 30000 : 15000; // More time for creation
            
            if (pollingState.pollCount >= maxPolls || timeElapsed > maxTime) {
              productsToRemove.push(productId);
            }
          }
        });

        // Remove completed/expired products
        if (productsToRemove.length > 0) {
          setProductPollingStates(prev => {
            const newMap = new Map(prev);
            productsToRemove.forEach(id => newMap.delete(id));
            return newMap;
          });
        }

        // Update poll counts for remaining products
        setProductPollingStates(prev => {
          const newMap = new Map(prev);
          newMap.forEach((state, productId) => {
            if (!productsToRemove.includes(productId)) {
              newMap.set(productId, { ...state, pollCount: state.pollCount + 1 });
            }
          });
          return newMap;
        });
        
        // Always invalidate cache after polling to ensure UI updates
        dispatch(productCatalogueApi.util.invalidateTags([
          { type: 'Product', id: 'LIST' }
        ]));
      }
    } catch (error) {
      console.error('Individual product polling error:', error);
      // On error, reduce polling frequency and continue
    }

    // Schedule next poll if there are still products to poll
    const remainingProducts = productPollingStates.size - productsToRemove.length;
    if (remainingProducts > 0) {
      console.log(`Scheduling next poll for ${remainingProducts} remaining products`);
      pollTimeoutRef.current = setTimeout(pollIndividualProducts, 3000);
    }
  }, [productPollingStates, refetch, dispatch]);

  // Bulk polling logic
  const pollBulkProducts = useCallback(async () => {
    if (!bulkPollingState.isPolling) return;

    const currentTime = Date.now();
    const timeElapsed = currentTime - bulkPollingState.startTime;

    // Stop if 30 seconds have elapsed
    if (timeElapsed > 30000) {
      clearBulkPolling();
      return;
    }

    try {
      const result = await refetch();
      
      if (result.data?.data) {
        const products = result.data.data;
        const stillPending = new Set<string>();

        // Check which products are still pending
        bulkPollingState.pendingProductIds.forEach(productId => {
          const product = products.find(p => p._id === productId);
          const hasRiskAnalysis = product?.riskAnalysis;
          const isAnalysisComplete = hasRiskAnalysis && 
              hasRiskAnalysis.riskLevel !== 'RUNNING' && 
              hasRiskAnalysis.riskLevel !== 'PENDING';
          
          if (!isAnalysisComplete) {
            stillPending.add(productId);
          }
        });

        // Update bulk polling state
        setBulkPollingState(prev => ({
          ...prev,
          pollCount: prev.pollCount + 1,
          pendingProductIds: stillPending
        }));

        // Invalidate cache to update UI
        dispatch(productCatalogueApi.util.invalidateTags([{ type: 'Product', id: 'LIST' }]));

        // Continue polling if there are still pending products
        if (stillPending.size > 0) {
          bulkPollTimeoutRef.current = setTimeout(pollBulkProducts, 3000);
        } else {
          clearBulkPolling();
        }
      }
    } catch (error) {
      console.error('Bulk polling error:', error);
      // Continue polling on error but log it
      if (bulkPollingState.pendingProductIds.size > 0) {
        bulkPollTimeoutRef.current = setTimeout(pollBulkProducts, 5000); // Slower retry on error
      }
    }
  }, [bulkPollingState, refetch, dispatch, clearBulkPolling]);

  // Start individual product polling when products are added
  useEffect(() => {
    if (productPollingStates.size > 0 && !pollTimeoutRef.current) {
      pollTimeoutRef.current = setTimeout(pollIndividualProducts, 3000);
    }
    
    // If no products to poll, clear any existing timeout
    if (productPollingStates.size === 0 && pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, [productPollingStates.size, pollIndividualProducts]);

  // Start bulk polling when bulk state changes
  useEffect(() => {
    if (bulkPollingState.isPolling && !bulkPollTimeoutRef.current) {
      // Start bulk polling after 3 seconds
      bulkPollTimeoutRef.current = setTimeout(pollBulkProducts, 3000);
    }
  }, [bulkPollingState.isPolling, pollBulkProducts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      if (bulkPollTimeoutRef.current) {
        clearTimeout(bulkPollTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Actions
    addProductPolling,
    addBulkPolling,
    
    // Helpers
    isProductPending
  };
};